import {
  faBoxArchive,
  faFileExport,
  faInboxOut,
  faPlus,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import type { CapaAction, CapaPlan } from '@/api/capa/types';
import { AuditCapaActionsTable } from '@/app/(client)/audits/-components/digitized/AuditCapaActionsTable';
import { AuditCapaPlansTable } from '@/app/(client)/audits/-components/digitized/AuditCapaPlansTable';
import {
  auditCapaActionsKeys,
  useAuditCapaActions,
} from '@/app/(client)/audits/-hooks/useAuditCapaActions';
import { useAuditCapaCellCommit } from '@/app/(client)/audits/-hooks/useAuditCapaCellCommit';
import {
  auditCapaPlansKeys,
  useAuditCapaPlans,
} from '@/app/(client)/audits/-hooks/useAuditCapaPlans';
import { useAuditDetail } from '@/app/(client)/audits/-hooks/useAuditDetail';
import { ExportFindingsDialog } from '@/app/(client)/capa/-components/ExportFindingsDialog';
import {
  canEditActionField,
  type EditableActionField,
  getActionRoleForUser,
} from '@/app/(client)/capa/-utils/actionPermissions';
import { CreatePlanDialog } from '@/components/shared/CreatePlanDialog';
import type { BulkActionDef } from '@/components/shared/DataTable/BulkActions';
import type { ToolbarActionDef } from '@/components/shared/DataTable/types';
import { PageContainer } from '@/components/shared/PageContainer';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useArchiveActionsMutation } from '@/hooks/useArchiveActionsMutation';
import { useArchivePlansMutation } from '@/hooks/useArchivePlansMutation';
import { useCreateEntityTagMutation } from '@/hooks/useCreateEntityTagMutation';
import { useCreatePlanDialog } from '@/hooks/useCreatePlanDialog';
import { useEntityTagsQuery } from '@/hooks/useEntityTagsQuery';
import { useImpactAreasQuery } from '@/hooks/useImpactAreasQuery';
import { useLinkEntityTagsMutation } from '@/hooks/useLinkEntityTagsMutation';
import { ensurePermissions, usePermissions } from '@/hooks/usePermissions';
import { useTranslator } from '@/hooks/useTranslator';
import { useUnarchiveActionsMutation } from '@/hooks/useUnarchiveActionsMutation';
import { useUnarchivePlansMutation } from '@/hooks/useUnarchivePlansMutation';
import { useSessionStore } from '@/store/sessionStore';
import { UpdateLinkedActionsDialog } from '../../../../../capa/-components/UpdateLinkedActionsDialog';

export const Route = createFileRoute(
  '/(client)/audits/(pages)/(detail)/$auditId/capa/',
)({
  beforeLoad: async ({ params }) => {
    const perms = await ensurePermissions();
    if (!perms.capa.canRead) {
      throw redirect({
        to: '/audits/$auditId/overview',
        params: { auditId: params.auditId },
      });
    }
  },
  component: AuditCapaPage,
});

function AuditCapaPage() {
  const { auditId } = Route.useParams();
  const { getTranslation: t } = useTranslator({ page: 'auditDetail' });
  const queryClient = useQueryClient();
  const perms = usePermissions().capa;
  const { data: audit } = useAuditDetail(auditId);
  const myUserId = useSessionStore(s => s.user?.id ?? '');
  const myCompanyId = useSessionStore(s => s.user?.companyId ?? '');

  // Opens on actions, not plans: from an audit the question is "what do I have
  // to do about this", and the actions are that answer. A plan is the container
  // they're grouped into, one level up from the work itself.
  const [view, setView] = useState<'plans' | 'actions'>('actions');
  const [isArchived, setIsArchived] = useState(false);
  const [createPlanOpen, setCreatePlanOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [actionSelection, setActionSelection] = useState<
    Record<string, boolean>
  >({});
  const [planSelection, setPlanSelection] = useState<Record<string, boolean>>(
    {},
  );

  const { data: plansData, isLoading: plansLoading } = useAuditCapaPlans(
    auditId,
    isArchived,
  );
  const { data: actionsData, isLoading: actionsLoading } = useAuditCapaActions(
    auditId,
    isArchived,
  );
  const { data: impactAreas } = useImpactAreasQuery();
  const { data: tagsData } = useEntityTagsQuery();
  const tags = tagsData?.data ?? [];

  const createTagMutation = useCreateEntityTagMutation();
  const linkTagsMutation = useLinkEntityTagsMutation();
  const archiveActionsMutation = useArchiveActionsMutation();
  const unarchiveActionsMutation = useUnarchiveActionsMutation();
  const archivePlansMutation = useArchivePlansMutation();
  const unarchivePlansMutation = useUnarchivePlansMutation();

  const createPlanController = useCreatePlanDialog(setCreatePlanOpen, {
    id: auditId,
    companyId: audit?.companyId ?? '',
    ...(audit?.name != null ? { name: audit.name } : {}),
    ...(audit?.companyName != null ? { companyName: audit.companyName } : {}),
  });

  const actionSelectedIds = Object.entries(actionSelection)
    .filter(([, v]) => v)
    .map(([id]) => id);
  const planSelectedIds = Object.entries(planSelection)
    .filter(([, v]) => v)
    .map(([id]) => id);

  // Inline-edit permission gates. Actions reuse the shared per-role util;
  // plans have no dedicated util, so gate on company ownership. Archived
  // view is always read-only.
  const canEditAction = (field: EditableActionField, row: CapaAction) =>
    perms.canEdit &&
    canEditActionField(
      field,
      getActionRoleForUser(row, { id: myUserId, companyId: myCompanyId }),
      isArchived,
    );
  const canEditPlan = (row: CapaPlan) =>
    perms.canEdit &&
    !isArchived &&
    !!myCompanyId &&
    row.ownerCompanyId === myCompanyId;

  const onCreateTag = async (
    title: string,
    colorCode: string,
    description: string,
  ) => {
    const res = await createTagMutation.mutateAsync({
      title,
      colorCode,
      description,
    });
    return { value: res.data.id, label: res.data.title };
  };

  const {
    handleActionCellCommit,
    handlePlanCellCommit,
    updateLinkedActionsPrompt,
  } = useAuditCapaCellCommit({ auditId, linkTagsMutation, tags });

  const handleArchiveActions = async () => {
    try {
      const mutation = isArchived
        ? unarchiveActionsMutation
        : archiveActionsMutation;
      await mutation.mutateAsync({ actionIds: actionSelectedIds });
      void queryClient.invalidateQueries({
        queryKey: auditCapaActionsKeys.base(auditId),
      });
      setActionSelection({});
    } catch (err) {
      toast.error(t('capa.updateError'), {
        description: (err as Error).message,
      });
    }
  };

  const handleArchivePlans = async () => {
    try {
      if (isArchived) {
        await unarchivePlansMutation.mutateAsync({
          actionIds: planSelectedIds,
        });
      } else {
        await archivePlansMutation.mutateAsync({
          actionIds: planSelectedIds,
          includeLinkedActions: false,
        });
      }
      void queryClient.invalidateQueries({
        queryKey: auditCapaPlansKeys.base(auditId),
      });
      setPlanSelection({});
    } catch (err) {
      toast.error(t('capa.updateError'), {
        description: (err as Error).message,
      });
    }
  };

  // The selection bar replaces the toolbar while rows are selected, so export
  // must also live here or exporting the selected rows becomes unreachable.
  const exportBulkAction: BulkActionDef = {
    id: 'export',
    type: 'button',
    label: t('capa.export'),
    icon: <FontAwesomeIcon icon={faFileExport} className='size-[13px]' />,
    onClick: () => setExportOpen(true),
  };

  const actionBulkActions: BulkActionDef[] = [
    ...(perms.canArchive
      ? [
          isArchived
            ? {
                id: 'unarchive',
                type: 'button' as const,
                label: t('capa.unarchive'),
                icon: (
                  <FontAwesomeIcon icon={faInboxOut} className='size-[13px]' />
                ),
                onClick: handleArchiveActions,
                loading: unarchiveActionsMutation.isPending,
              }
            : {
                id: 'archive',
                type: 'button' as const,
                label: t('capa.archive'),
                icon: (
                  <FontAwesomeIcon
                    icon={faBoxArchive}
                    className='size-[13px]'
                  />
                ),
                onClick: handleArchiveActions,
                loading: archiveActionsMutation.isPending,
              },
        ]
      : []),
    ...(perms.canRead ? [exportBulkAction] : []),
  ];

  const planBulkActions: BulkActionDef[] = [
    ...(perms.canArchive
      ? [
          isArchived
            ? {
                id: 'unarchive',
                type: 'button' as const,
                label: t('capa.unarchive'),
                icon: (
                  <FontAwesomeIcon icon={faInboxOut} className='size-[13px]' />
                ),
                onClick: handleArchivePlans,
                loading: unarchivePlansMutation.isPending,
              }
            : {
                id: 'archive',
                type: 'button' as const,
                label: t('capa.archive'),
                icon: (
                  <FontAwesomeIcon
                    icon={faBoxArchive}
                    className='size-[13px]'
                  />
                ),
                onClick: handleArchivePlans,
                loading: archivePlansMutation.isPending,
              },
        ]
      : []),
    ...(perms.canRead ? [exportBulkAction] : []),
  ];

  const exportSelectedIds =
    view === 'actions' ? actionSelectedIds : planSelectedIds;

  // Plans/Actions section toggle — styled like the active/archive tabs and
  // rendered inside the table toolbar's top-left corner (leadingToolbar slot).
  const sectionToggle = (
    <ToggleGroup
      value={[view]}
      onValueChange={v => {
        const next = v[v.length - 1];
        if (next === 'plans' || next === 'actions') setView(next);
      }}
    >
      <ToggleGroupItem
        value='plans'
        className='text-xs px-3 hover:bg-brand-accent/10 aria-pressed:font-medium'
      >
        {t('capa.plans')}
      </ToggleGroupItem>
      <ToggleGroupItem
        value='actions'
        className='text-xs px-3 hover:bg-brand-accent/10 aria-pressed:font-medium'
      >
        {t('capa.actions')}
      </ToggleGroupItem>
    </ToggleGroup>
  );

  // Main toolbar CTAs — rendered inside the table toolbar and sized like other
  // tables: primary Create plan + secondary Export.
  const capaToolbarActions: ToolbarActionDef[] = [
    ...(perms.canCreate
      ? [
          {
            id: 'create-plan',
            type: 'button' as const,
            variant: 'default' as const,
            label: t('capa.createPlan'),
            icon: <FontAwesomeIcon icon={faPlus} />,
            onClick: () => setCreatePlanOpen(true),
          },
        ]
      : []),
    // No explicit variant: secondary to create-plan when that's visible, but
    // correctly becomes the one emphasised action when it isn't (read-only
    // user) instead of forcing outline.
    ...(perms.canRead
      ? [
          {
            id: 'export',
            type: 'button' as const,
            label: t('capa.export'),
            icon: <FontAwesomeIcon icon={faFileExport} />,
            onClick: () => setExportOpen(true),
          },
        ]
      : []),
  ];

  // Active/Archived toggle under the main toolbar row — mirrors the
  // questionnaires table's active/archive tabs.
  const archiveToggle = (
    <ToggleGroup
      value={[isArchived ? 'archived' : 'active']}
      onValueChange={v => {
        const next = v[v.length - 1];
        if (!next) return;
        setIsArchived(next === 'archived');
        setActionSelection({});
        setPlanSelection({});
      }}
    >
      <ToggleGroupItem
        value='active'
        className='text-xs px-3 hover:bg-brand-accent/10 aria-pressed:font-medium'
      >
        {t('capa.active')}
      </ToggleGroupItem>
      <ToggleGroupItem
        value='archived'
        className='text-xs px-3 hover:bg-brand-accent/10 aria-pressed:font-medium'
      >
        {t('capa.archived')}
      </ToggleGroupItem>
    </ToggleGroup>
  );

  return (
    // The table is this page's main entity, so it owns the full remaining
    // viewport height — see the full-height table page rule in
    // `frontend-v2/CLAUDE.md`. `PageContainer` supplies the
    // `flex flex-col flex-1 min-h-0 overflow-hidden` chain the DataTable's
    // `flex-1` root needs to fill and to keep its pagination footer pinned.
    <PageContainer>
      {view === 'plans' ? (
        <AuditCapaPlansTable
          plans={plansData?.data ?? []}
          impactAreas={impactAreas ?? []}
          isLoading={plansLoading}
          t={t}
          tags={tags}
          onCreateTag={onCreateTag}
          canEdit={canEditPlan}
          onCellCommit={handlePlanCellCommit}
          rowSelection={planSelection}
          onRowSelectionChange={setPlanSelection}
          bulkActions={planBulkActions}
          leadingToolbar={sectionToggle}
          toolbarActions={capaToolbarActions}
          subTabs={archiveToggle}
        />
      ) : (
        <AuditCapaActionsTable
          actions={actionsData?.data ?? []}
          impactAreas={impactAreas ?? []}
          isLoading={actionsLoading}
          t={t}
          tags={tags}
          onCreateTag={onCreateTag}
          canEditField={canEditAction}
          onCellCommit={handleActionCellCommit}
          rowSelection={actionSelection}
          onRowSelectionChange={setActionSelection}
          bulkActions={actionBulkActions}
          leadingToolbar={sectionToggle}
          toolbarActions={capaToolbarActions}
          subTabs={archiveToggle}
        />
      )}

      {/* Keyed by view: the dialog hook seeds its locked-entity state from
          entityType at mount, so switching plans/actions must remount it. */}
      <ExportFindingsDialog
        key={view}
        open={exportOpen}
        onOpenChange={setExportOpen}
        selectedIds={exportSelectedIds}
        entityType={view === 'actions' ? 'ACTION' : 'CAP'}
      />

      <CreatePlanDialog
        open={createPlanOpen}
        onOpenChange={setCreatePlanOpen}
        controller={createPlanController}
      />

      <UpdateLinkedActionsDialog {...updateLinkedActionsPrompt} />
    </PageContainer>
  );
}
